/**
 * Registration page — email signup with OTP verification, plus Google.
 *
 * Route: /auth/register
 * States: idle → loading → verify-email → auto-login → error
 */

import { LitElement, html, css, nothing } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { authService } from '../../services/auth/auth-service.js'
import { authConfig } from '../../services/auth/auth-config.js'
import { CognitoError } from '../../services/auth/cognito-api.js'

import '@material/web/button/filled-button.js'
import '@material/web/button/outlined-button.js'
import '@material/web/button/text-button.js'
import '@material/web/textfield/outlined-text-field.js'
import '@material/web/icon/icon.js'
import '@material/web/progress/circular-progress.js'

import './auth-otp-verify.js'

type RegisterState = 'idle' | 'loading' | 'verify-email' | 'error'

@customElement('auth-register')
export class AuthRegister extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;
      padding: var(--cairn-spacing-xl, 32px) var(--cairn-spacing-md, 16px);
      box-sizing: border-box;
    }

    .container {
      max-width: 400px;
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .logo {
      width: 72px;
      height: 72px;
      margin-bottom: var(--cairn-spacing-lg, 24px);
    }

    .logo img {
      width: 100%;
      height: 100%;
    }

    h2 {
      font-size: var(--cairn-text-size-xl, 24px);
      font-weight: var(--cairn-font-weight-bold, 600);
      color: var(--cairn-text-color, #1e293b);
      margin: 0 0 var(--cairn-spacing-xs, 4px);
    }

    .subtitle {
      font-size: var(--cairn-text-size-md, 16px);
      color: var(--cairn-text-secondary-color, #64748b);
      margin: 0 0 var(--cairn-spacing-xl, 32px);
    }

    md-outlined-text-field {
      width: 100%;
      margin-bottom: var(--cairn-spacing-md, 16px);
      --md-outlined-text-field-container-shape: var(--cairn-border-radius, 8px);
    }

    .actions {
      display: flex;
      flex-direction: column;
      gap: var(--cairn-spacing-sm, 8px);
      width: 100%;
      margin-bottom: var(--cairn-spacing-md, 16px);
    }

    md-filled-button,
    md-outlined-button {
      width: 100%;
      --md-filled-button-container-height: 48px;
      --md-filled-button-container-shape: var(--cairn-border-radius, 8px);
      --md-outlined-button-container-height: 48px;
      --md-outlined-button-container-shape: var(--cairn-border-radius, 8px);
    }

    .divider {
      display: flex;
      align-items: center;
      gap: var(--cairn-spacing-md, 16px);
      width: 100%;
      margin: var(--cairn-spacing-md, 16px) 0;
      color: var(--cairn-text-muted-color, #94a3b8);
      font-size: var(--cairn-text-size-sm, 14px);
    }

    .divider::before,
    .divider::after {
      content: '';
      flex: 1;
      border-top: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    .error-message {
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-error-color, #dc2626);
      margin: 0 0 var(--cairn-spacing-md, 16px);
      text-align: center;
    }

    .footer {
      margin-top: var(--cairn-spacing-lg, 24px);
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-secondary-color, #64748b);
      text-align: center;
    }

    .loading-overlay {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--cairn-spacing-md, 16px);
      padding: var(--cairn-spacing-xl, 32px);
      color: var(--cairn-text-secondary-color, #64748b);
    }
  `

  @state() private registerState: RegisterState = 'idle'
  @state() private email = ''
  @state() private error = ''
  private signUpSession: string | undefined

  override connectedCallback(): void {
    super.connectedCallback()
    const params = new URLSearchParams(window.location.search)
    const inviteToken = params.get('invite')
    if (inviteToken) {
      sessionStorage.setItem('pendingInviteToken', inviteToken)
    }
  }

  private async handleEmailSubmit(e: Event): Promise<void> {
    e.preventDefault()
    const email = this.email.trim().toLowerCase()
    if (!email) return

    this.registerState = 'loading'
    this.error = ''

    try {
      this.signUpSession = await authService.register(email)
      this.registerState = 'verify-email'
    } catch (err) {
      if (err instanceof CognitoError && err.code === 'UsernameExistsException') {
        this.error = 'An account with this email already exists. Sign in instead?'
        this.registerState = 'error'
        return
      }
      this.registerState = 'error'
      this.error = this.formatError(err)
    }
  }

  private async handleOtpSubmitted(e: CustomEvent<{ code: string }>): Promise<void> {
    this.error = ''
    const email = this.email.trim().toLowerCase()

    try {
      await authService.confirmRegistration(email, e.detail.code, this.signUpSession)

      const { router } = await import('../../utils/router.js')
      const pendingToken = sessionStorage.getItem('pendingInviteToken')
      if (authService.needsSetup()) {
        router.navigate('/auth/setup')
      } else if (pendingToken) {
        sessionStorage.removeItem('pendingInviteToken')
        router.navigate(`/teams/invite/${pendingToken}`)
      } else if (authService.needsEula()) {
        router.navigate('/auth/eula')
      } else {
        router.navigate('/')
      }
    } catch (err) {
      this.error = this.formatError(err)
    }
  }

  private async handleOtpResend(): Promise<void> {
    const email = this.email.trim().toLowerCase()
    try {
      await authService.resendRegistrationCode(email)
      this.error = ''
    } catch (err) {
      this.error = this.formatError(err)
    }
  }

  private handleGoogleLogin(): void {
    void authService.loginWithGoogle()
  }

  private handleEmailInput(e: Event): void {
    this.email = (e.target as HTMLInputElement).value
    if (this.registerState === 'error') {
      this.registerState = 'idle'
      this.error = ''
    }
  }

  private handleEmailKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter') {
      void this.handleEmailSubmit(e)
    }
  }

  private navigateToLogin(): void {
    void import('../../utils/router.js').then(({ router }) => {
      router.navigate('/auth/login')
    })
  }

  private formatError(err: unknown): string {
    if (err instanceof CognitoError) {
      switch (err.code) {
        case 'UsernameExistsException':
          return 'An account with this email already exists. Sign in instead?'
        case 'CodeMismatchException':
          return 'Incorrect code. Please try again.'
        case 'ExpiredCodeException':
          return 'Code expired. We sent a new one.'
        case 'LimitExceededException':
          return 'Too many attempts. Please wait a moment.'
        case 'InvalidParameterException':
          return 'Please enter a valid email address.'
        default:
          return err.message
      }
    }
    return 'Something went wrong. Please try again.'
  }

  override render(): unknown {
    if (!authConfig.isConfigured) {
      return html`<div class="container">
        <p class="error-message">Authentication is not configured.</p>
      </div>`
    }

    return html`
      <div class="container">
        <div class="logo">
          <img src="/images/cairn-icon-clean.png" alt="Taselith" />
        </div>
        <h2>Create your account</h2>
        <p class="subtitle">Get started with Taselith</p>

        ${this.registerState === 'loading' ? this.renderLoading() : nothing}
        ${this.registerState === 'verify-email' ? this.renderVerifyEmail() : nothing}
        ${this.registerState === 'idle' || this.registerState === 'error'
          ? this.renderEmailForm()
          : nothing}
      </div>
    `
  }

  private renderLoading(): unknown {
    return html`
      <div class="loading-overlay">
        <md-circular-progress indeterminate></md-circular-progress>
        <span>Creating account...</span>
      </div>
    `
  }

  private renderVerifyEmail(): unknown {
    return html`
      <auth-otp-verify
        .email=${this.email.trim()}
        .error=${this.error}
        .length=${6}
        @otp-submitted=${this.handleOtpSubmitted}
        @otp-resend=${this.handleOtpResend}
      ></auth-otp-verify>
    `
  }

  private renderEmailForm(): unknown {
    return html`
      ${this.error ? html`<p class="error-message">${this.error}</p>` : nothing}

      <md-outlined-text-field
        label="Email"
        type="email"
        .value=${this.email}
        @input=${this.handleEmailInput}
        @keydown=${this.handleEmailKeydown}
      ></md-outlined-text-field>

      <div class="actions">
        <md-filled-button @click=${this.handleEmailSubmit}> Create account </md-filled-button>
      </div>

      <div class="divider">or</div>

      <div class="actions">
        <md-outlined-button @click=${this.handleGoogleLogin}>
          Continue with Google
        </md-outlined-button>
      </div>

      <div class="footer">
        Already have an account?
        <md-text-button @click=${this.navigateToLogin}>Sign in</md-text-button>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'auth-register': AuthRegister
  }
}
